import React, { useState, useEffect } from "react";
import {
  X,
  Mail,
  Phone,
  Briefcase,
  User,
  CheckCircle,
  AlertCircle,
  Clock,
} from "lucide-react";
import { professionalInvitationAPI } from "../../services/api";
import Button from "./Button";
import { Select } from "./FormControls";

const SUPPORTED_PROFESSIONS = [
  "CA",
  "CS",
  "CMA",
  "Advocate",
  "Consultant",
  "Other",
];

const InviteProfessionalModal = ({
  isOpen,
  onClose,
  onProfessionalCreated,
  onProfessionalSelected,
}) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    profession: "",
    mobile: "",
    membershipNo: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | 'duplicate'
  const [duplicateProfessional, setDuplicateProfessional] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [invitationUrl, setInvitationUrl] = useState("");

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        fullName: "",
        email: "",
        profession: "",
        mobile: "",
        membershipNo: "",
      });
      setErrors({});
      setSubmitStatus(null);
      setDuplicateProfessional(null);
      setShowSuccess(false);
      setInvitationUrl("");
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.profession) {
      newErrors.profession = "Profession is required";
    }

    if (formData.mobile && !/^[6-9]\d{9}$/.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Name/value form of the update, so controls that report a plain value
  // (the custom Select) share one path with the native inputs.
  const setField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleInputChange = (e) => setField(e.target.name, e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response =
        await professionalInvitationAPI.quickCreateProfessional(formData);

      if (response.success) {
        const professional = response.data.professional;

        if (professional.existing) {
          // Handle duplicate case
          setSubmitStatus("duplicate");
          setDuplicateProfessional(professional);

          // Auto-select the existing professional after a delay
          setTimeout(() => {
            onProfessionalSelected && onProfessionalSelected(professional);
            handleClose();
          }, 2000);
        } else {
          // Handle new invitation
          setSubmitStatus("success");
          setShowSuccess(true);
          // Capture invitation URL so corporate user can copy if email fails
          if (response.data?.invitationUrl) {
            setInvitationUrl(response.data.invitationUrl);
          }

          // Auto-select the invited professional for assignment
          setTimeout(() => {
            onProfessionalCreated && onProfessionalCreated(professional);
            onProfessionalSelected && onProfessionalSelected(professional);
            handleClose();
          }, 2500); // Slightly longer so user can copy link
        }
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error inviting professional:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const getStatusIcon = () => {
    switch (submitStatus) {
      case "success":
        return <CheckCircle className="w-5 h-5" style={{ color: '#4a6b3f' }} />;
      case "duplicate":
        return <AlertCircle className="w-5 h-5" style={{ color: '#8a6a1f' }} />;
      case "error":
        return <AlertCircle className="w-5 h-5" style={{ color: '#9a564a' }} />;
      default:
        return null;
    }
  };

  const getStatusMessage = () => {
    switch (submitStatus) {
      case "success":
        return "Professional invited successfully! They will receive an email shortly.";
      case "duplicate":
        return `Professional already exists. Auto-selecting ${duplicateProfessional?.fullName || "professional"}...`;
      case "error":
        return "Failed to invite professional. Please try again.";
      default:
        return "";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
          onClick={handleClose}
        />

        <div className="relative bg-white rounded-[14px] shadow-xl max-w-md w-full border border-[#ddd6c6]">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#eee6d8]">
            <h2 className="text-[18px] font-bold" style={{ color: '#08111f' }}>
              Invite New Professional
            </h2>
            <button
              type="button"
              aria-label="Close"
              title="Close"
              onClick={handleClose}
              disabled={isSubmitting}
              className="group w-8 h-8 rounded-[10px] flex items-center justify-center text-[#7a7060] transition-all duration-200 hover:bg-[#9a564a]/10 hover:text-[#9a564a] active:scale-90 disabled:opacity-50"
            >
              <X className="w-[17px] h-[17px] transition-transform duration-300 group-hover:rotate-90" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 pb-6">
            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="flex items-center text-[12px] font-semibold mb-2" style={{ color: '#08111f' }}>
                  <User className="w-4 h-4 mr-2" style={{ color: '#145f5f' }} />
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2.5 rounded-[10px] border outline-none transition-colors text-[13px] ${
                    errors.fullName 
                      ? 'border-[#e6d2cb] focus:border-[#9a564a]' 
                      : 'border-[#ddd6c6] focus:border-[#145f5f]'
                  }`}
                  placeholder="Enter professional's full name"
                  disabled={isSubmitting}
                />
                {errors.fullName && (
                  <p className="mt-1 text-[11px]" style={{ color: '#9a564a' }}>{errors.fullName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="flex items-center text-[12px] font-semibold mb-2" style={{ color: '#08111f' }}>
                  <Mail className="w-4 h-4 mr-2" style={{ color: '#145f5f' }} />
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2.5 rounded-[10px] border outline-none transition-colors text-[13px] ${
                    errors.email 
                      ? 'border-[#e6d2cb] focus:border-[#9a564a]' 
                      : 'border-[#ddd6c6] focus:border-[#145f5f]'
                  }`}
                  placeholder="professional@example.com"
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="mt-1 text-[11px]" style={{ color: '#9a564a' }}>{errors.email}</p>
                )}
              </div>

              {/* Profession */}
              <div>
                <label className="flex items-center text-[12px] font-semibold mb-2" style={{ color: '#08111f' }}>
                  <Briefcase className="w-4 h-4 mr-2" style={{ color: '#145f5f' }} />
                  Profession *
                </label>
                <Select
                  name="profession"
                  value={formData.profession}
                  onChange={(v) => setField("profession", v)}
                  placeholder="Select profession"
                  options={SUPPORTED_PROFESSIONS}
                  invalid={Boolean(errors.profession)}
                  disabled={isSubmitting}
                />
                {errors.profession && (
                  <p className="mt-1 text-[11px]" style={{ color: '#9a564a' }}>
                    {errors.profession}
                  </p>
                )}
              </div>

              {/* Mobile (Optional) */}
              <div>
                <label className="flex items-center text-[12px] font-semibold mb-2" style={{ color: '#08111f' }}>
                  <Phone className="w-4 h-4 mr-2" style={{ color: '#145f5f' }} />
                  Mobile (Optional)
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2.5 rounded-[10px] border outline-none transition-colors text-[13px] ${
                    errors.mobile 
                      ? 'border-[#e6d2cb] focus:border-[#9a564a]' 
                      : 'border-[#ddd6c6] focus:border-[#145f5f]'
                  }`}
                  placeholder="10-digit mobile number"
                  disabled={isSubmitting}
                />
                {errors.mobile && (
                  <p className="mt-1 text-[11px]" style={{ color: '#9a564a' }}>{errors.mobile}</p>
                )}
                <p className="mt-1 text-[10.5px]" style={{ color: '#a39a88' }}>
                  Mobile number can be added later by the professional
                </p>
              </div>

              {/* Membership Number (Optional) */}
              <div>
                <label className="flex items-center text-[12px] font-semibold mb-2" style={{ color: '#08111f' }}>
                  <Briefcase className="w-4 h-4 mr-2" style={{ color: '#145f5f' }} />
                  Membership Number (Optional)
                </label>
                <input
                  type="text"
                  name="membershipNo"
                  value={formData.membershipNo}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2.5 rounded-[10px] border border-[#ddd6c6] outline-none transition-colors text-[13px] focus:border-[#145f5f]"
                  placeholder="CA/CS/CMA membership number"
                  disabled={isSubmitting}
                />
                <p className="mt-1 text-[10.5px]" style={{ color: '#a39a88' }}>
                  Membership number can be added later by the professional
                </p>
              </div>
            </div>

            {/* Status Message */}
            {submitStatus && (
              <div 
                className="mt-4 px-3 py-2.5 rounded-[10px] flex items-center space-x-2" 
                style={{
                  backgroundColor: submitStatus === 'success' ? '#e8efe5' :
                                 submitStatus === 'duplicate' ? '#f3ebd7' : '#f2e9e6'
                }}
              >
                {getStatusIcon()}
                <p className="text-[11.5px] font-medium" style={{
                  color: submitStatus === 'success' ? '#4a6b3f' :
                         submitStatus === 'duplicate' ? '#8a6a1f' : '#9a564a'
                }}>
                  {getStatusMessage()}
                </p>
              </div>
            )}

            {/* Invitation URL (for copying) */}
            {showSuccess && invitationUrl && (
              <div className="mt-4 p-3 rounded-[10px]" style={{ backgroundColor: '#f7f4ec' }}>
                <p className="text-[11.5px] mb-2" style={{ color: '#7a7060' }}>
                  Invitation link (copy if email fails):
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={invitationUrl}
                    className="flex-1 px-2 py-1.5 text-[11px] bg-white border border-[#ddd6c6] rounded-[6px]"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(invitationUrl);
                      // Flash feedback
                      const btn = document.activeElement;
                      if (btn) {
                        btn.textContent = "Copied!";
                        setTimeout(() => (btn.textContent = "Copy"), 1500);
                      }
                    }}
                    className="px-2.5 py-1.5 text-[11px] font-semibold text-white rounded-[6px] transition-colors"
                    style={{ backgroundColor: '#145f5f' }}
                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#0d4f4f'; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#145f5f'; }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={showSuccess}
                loading={isSubmitting}
                leftIcon={!isSubmitting && showSuccess ? <CheckCircle className="w-4 h-4" /> : null}
              >
                {isSubmitting ? 'Inviting...' : showSuccess ? 'Invited!' : 'Send Invitation'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteProfessionalModal;
